import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Globe, Loader2, Check } from "lucide-react";

/**
 * B2B order dialog — pick the supplier the order was placed with on a B2B portal,
 * then mark the request as "Sipariş Verildi" and notify the requester.
 * Props: open, onOpenChange, request, onDone?
 */
export default function B2BOrderDialog({ open, onOpenChange, request, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        setSuppliers(data);
      } catch (_) {}
    })();
    if (request) setSupplierId(request.supplier_id || "");
  }, [open, request]);

  const confirm = async () => {
    if (!supplierId) { toast.error("Lütfen tedarikçi seçin"); return; }
    setBusy(true);
    try {
      const supplierName = suppliers.find((s) => s.id === supplierId)?.name || request.supplier_name || "";
      await api.post(`/requests/${request.id}/mark-sent`, {
        channel: "b2b",
        supplier_id: supplierId,
        supplier_name: supplierName,
      });
      toast.success("B2B siparişi kaydedildi — talep 'Sipariş Verildi' olarak işaretlendi");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="b2b-order-dialog" className="max-w-md p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Globe className="h-5 w-5 text-indigo-600" /> B2B'den Sipariş Ver
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Siparişi B2B portalından verdiğiniz tedarikçiyi seçin. Onayladığınızda talep "Sipariş Verildi" olarak işaretlenir ve talep sahibine bildirim gönderilir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="b2b-supplier-select" className="mt-1.5 h-11">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`b2b-supplier-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="w-full sm:w-auto">
            Vazgeç
          </Button>
          <Button
            data-testid="b2b-confirm-btn"
            onClick={confirm}
            disabled={busy || !supplierId}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Onayla (OK)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
